import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, Eye, Loader2, Scale, Send, Trash2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export default function Gdpr() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [services, setServices] = useState([]);
  const [reqType, setReqType] = useState("erasure");
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => navigate("/"))
      .finally(() => setChecking(false));
  }, [location.state, navigate]);

  const loadRequests = () => {
    axios.get(`${API}/gdpr/requests`, { withCredentials: true }).then((r) => setRequests(r.data.requests)).catch(() => setRequests([]));
  };

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/gdpr/services`, { withCredentials: true }).then((r) => setServices(r.data.services)).catch(() => {});
    loadRequests();
  }, [user]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const send = async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/gdpr/requests`, { service_ids: [...selected], type: reqType }, { withCredentials: true });
      toast.success(t("gdpr.sentToast", { count: selected.size }));
      setSelected(new Set());
      loadRequests();
    } catch (e) {
      toast.error(t("gdpr.sendError"));
    } finally {
      setSending(false);
    }
  };

  const complete = async (id) => {
    try {
      await axios.patch(`${API}/gdpr/requests/${id}/complete`, {}, { withCredentials: true });
      loadRequests();
    } catch (e) {
      toast.error(t("gdpr.sendError"));
    }
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-mint animate-spin" strokeWidth={1.5} />
      </main>
    );
  }

  const deadlineOf = (createdAt) => {
    const d = new Date(createdAt);
    d.setDate(d.getDate() + 30);
    return fmtDate(d);
  };

  return (
    <main data-testid="gdpr-page">
      <Header />
      <div className="pt-32 pb-24 max-w-4xl mx-auto px-6 lg:px-10 min-h-screen">
        <Link to="/account" data-testid="gdpr-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-mint transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t("gdpr.back")}
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-mint/10 border border-mint/20 flex items-center justify-center">
            <Scale className="w-6 h-6 text-mint" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl">{t("gdpr.title")}</h1>
            <p className="mt-1 text-sm text-white/50">{t("gdpr.subtitle")}</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mt-10">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-3">{t("gdpr.stepType")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { id: "access", icon: Eye },
              { id: "erasure", icon: Trash2 },
            ].map((rt) => (
              <button
                key={rt.id}
                onClick={() => setReqType(rt.id)}
                data-testid={`gdpr-type-${rt.id}`}
                className={`text-left rounded-2xl border p-6 transition-colors duration-200 ${
                  reqType === rt.id ? "border-mint/50 bg-mint/10" : "border-white/10 bg-card hover:border-white/25"
                }`}
              >
                <rt.icon className={`w-5 h-5 ${reqType === rt.id ? "text-mint" : "text-white/40"}`} strokeWidth={1.5} />
                <p className="mt-3 font-display font-semibold text-white">{t(`gdpr.type.${rt.id}.title`)}</p>
                <p className="mt-1 text-xs text-white/50 leading-relaxed">{t(`gdpr.type.${rt.id}.desc`)}</p>
              </button>
            ))}
          </div>

          <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mt-10 mb-3">{t("gdpr.stepServices")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gdpr-services-grid">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => toggle(s.id)}
                data-testid={`gdpr-service-${s.id}`}
                className={`rounded-xl border px-4 py-3.5 text-sm text-left transition-colors duration-200 ${
                  selected.has(s.id) ? "border-mint/60 bg-mint/10 text-white" : "border-white/10 bg-card text-white/60 hover:border-white/25"
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  {s.name}
                  {selected.has(s.id) && <CheckCircle2 className="w-4 h-4 text-mint shrink-0" strokeWidth={2} />}
                </span>
              </button>
            ))}
          </div>

          <button
            onClick={send}
            disabled={selected.size === 0 || sending}
            data-testid="gdpr-send-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />}
            {sending ? t("gdpr.sending") : t("gdpr.sendCta", { count: selected.size })}
          </button>
          <p className="mt-3 text-xs text-white/35">{t("gdpr.sendHint")}</p>
        </motion.div>

        <div className="mt-16">
          <h2 className="font-display font-semibold text-white text-xl mb-4">{t("gdpr.trackerTitle")}</h2>
          <div className="space-y-3" data-testid="gdpr-requests-list">
            {requests === null ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-mint animate-spin" strokeWidth={1.5} /></div>
            ) : requests.length === 0 ? (
              <p data-testid="gdpr-empty" className="rounded-2xl border border-white/8 bg-card p-10 text-center text-sm text-white/40">{t("gdpr.empty")}</p>
            ) : (
              requests.map((r) => (
                <div key={r.id} data-testid={`gdpr-request-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-card px-5 py-4">
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-sm text-white/85">{r.service_name}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">
                      {t(`gdpr.type.${r.type}.short`)} · {fmtDate(r.created_at)}
                      {r.status === "sent" && <span className="text-white/35"> · {t("gdpr.deadline", { date: deadlineOf(r.created_at) })}</span>}
                    </p>
                  </div>
                  {r.status === "completed" ? (
                    <span data-testid={`gdpr-status-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-mint/15 text-mint px-3 py-1 text-[11px] font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                      {t("gdpr.statusDone")}
                    </span>
                  ) : r.status === "sent" ? (
                    <>
                      <span data-testid={`gdpr-status-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-sky-400/15 text-sky-300 px-3 py-1 text-[11px] font-semibold">
                        <Clock className="w-3.5 h-3.5" strokeWidth={2} />
                        {t("gdpr.statusSent")}
                      </span>
                      {r.days_left != null && (
                        <span
                          data-testid={`gdpr-countdown-${r.id}`}
                          className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                            r.days_left > 7 ? "bg-sky-400/15 text-sky-300" : r.days_left >= 0 ? "bg-amber-400/15 text-amber-300" : "bg-red-400/15 text-red-300"
                          }`}
                        >
                          {r.days_left >= 0 ? t("gdpr.daysLeft", { count: r.days_left }) : t("gdpr.overdue", { count: -r.days_left })}
                        </span>
                      )}
                      <button
                        onClick={() => complete(r.id)}
                        data-testid={`gdpr-complete-${r.id}`}
                        className="text-[11px] text-white/45 hover:text-mint underline underline-offset-2 transition-colors duration-200"
                      >
                        {t("gdpr.markDone")}
                      </button>
                    </>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-400/15 text-red-300 px-3 py-1 text-[11px] font-semibold">{t("gdpr.statusFailed")}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
